import React, { useEffect, useState } from 'react';
import { usePWA } from '../../hooks/usePWA';
import toast from 'react-hot-toast';

interface NotificationData {
  title: string;
  body: string;
  type: 'emergency' | 'disaster' | 'community' | 'general';
  location?: string;
  severity?: 'low' | 'medium' | 'high' | 'critical';
  url?: string;
}

const PushNotificationManager: React.FC = () => {
  const { capabilities, registerForPushNotifications } = usePWA();
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);
  const [swRegistration, setSwRegistration] = useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    // Initialize service worker registration first
    initializeServiceWorker();
  }, []);

  useEffect(() => {
    if (capabilities.hasNotificationPermission && capabilities.supportsPushNotifications && swRegistration) {
      initializePushNotifications();
    }
  }, [capabilities.hasNotificationPermission, capabilities.supportsPushNotifications, swRegistration]);

  const initializeServiceWorker = async () => {
    if ('serviceWorker' in navigator) {
      try {
        // Check if already registered
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          setSwRegistration(registration);
          console.log('Service Worker already registered');
        } else {
          // Register new service worker (fallback for environments without SW support)
          console.log('Service Worker not supported in this environment');
          // Create a minimal mock registration for notification functionality
          const mockRegistration = {
            showNotification: (title: string, options?: NotificationOptions) => {
              // Use browser notification API directly
              if ('Notification' in window && Notification.permission === 'granted') {
                // Remove actions from options for direct browser notifications
                const browserOptions = { ...options };
                delete browserOptions.actions;
                return new Notification(title, browserOptions);
              }
            }
          } as ServiceWorkerRegistration;
          setSwRegistration(mockRegistration);
        }
      } catch (error) {
        console.error('Service Worker initialization failed:', error);
        // Fallback to direct browser notifications
        const mockRegistration = {
          showNotification: (title: string, options?: NotificationOptions) => {
            if ('Notification' in window && Notification.permission === 'granted') {
              const browserOptions = { ...options };
              delete browserOptions.actions;
              return new Notification(title, browserOptions);
            }
          }
        } as ServiceWorkerRegistration;
        setSwRegistration(mockRegistration);
      }
    }
  };

  const initializePushNotifications = async () => {
    try {
      const sub = await registerForPushNotifications();
      if (sub) {
        setSubscription(sub);
        
        // Send subscription to server
        await sendSubscriptionToServer(sub);
        
        console.log('Push notifications initialized');
        toast.success('Push notifications enabled');
      }
    } catch (error) {
      console.error('Failed to initialize push notifications:', error);
      toast.error('Failed to enable push notifications');
    }
  };

  const sendSubscriptionToServer = async (subscription: PushSubscription) => {
    try {
      // Mock API call since we're in development
      console.log('Subscription would be sent to server:', {
        subscription: subscription.toJSON(),
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      });
      
      // Simulate successful server response
      return Promise.resolve();
    } catch (error) {
      console.error('Error saving push subscription:', error);
    }
  };

  const showLocalNotification = async (data: NotificationData) => {
    if (!capabilities.hasNotificationPermission) {
      console.warn('No notification permission');
      return;
    }

    try {
      const options: NotificationOptions = {
        body: data.body,
        icon: '/icons/icon-192x192.png',
        badge: '/icons/badge-72x72.png',
        vibrate: getVibrationPattern(data.type, data.severity),
        data: {
          type: data.type,
          location: data.location,
          severity: data.severity,
          url: data.url,
          timestamp: Date.now()
        },
        requireInteraction: data.type === 'emergency' || data.severity === 'critical',
        silent: false,
        tag: `${data.type}-${Date.now()}` // Prevent duplicate notifications
      };

      // Add image for disaster notifications
      if (data.type === 'disaster' && data.location) {
        options.image = '/images/disaster-notification.jpg';
      }

      // Use service worker registration if available, otherwise fallback to direct notification
      if (swRegistration && swRegistration.showNotification) {
        // For service worker notifications, we can include actions
        const swOptions = {
          ...options,
          actions: getNotificationActions(data.type)
        };
        await swRegistration.showNotification(data.title, swOptions);
      } else {
        // Direct browser notification (no actions supported)
        new Notification(data.title, options);
      }
    } catch (error) {
      console.error('Failed to show notification:', error);
      // Fallback to toast notification
      toast(`${data.title}: ${data.body}`, {
        icon: data.type === 'emergency' ? '🚨' : data.type === 'disaster' ? '⚠️' : 'ℹ️',
        duration: data.type === 'emergency' ? 10000 : 5000
      });
    }
  };

  const getVibrationPattern = (type: string, severity?: string): number[] => {
    switch (type) {
      case 'emergency':
        return [300, 100, 300, 100, 300]; // Urgent pattern
      case 'disaster':
        if (severity === 'critical') {
          return [200, 100, 200, 100, 200];
        }
        return [200, 100, 200]; // Standard disaster pattern
      case 'community':
        return [100]; // Single short vibration
      default:
        return [100, 50, 100]; // Default pattern
    }
  };

  const getNotificationActions = (type: string): NotificationAction[] => {
    const baseActions: NotificationAction[] = [
      {
        action: 'view',
        title: 'View Details'
      },
      {
        action: 'dismiss',
        title: 'Dismiss'
      }
    ];

    if (type === 'emergency') {
      return [
        {
          action: 'call-911',
          title: 'Call 911'
        },
        ...baseActions
      ];
    }

    if (type === 'disaster') {
      return [
        {
          action: 'safety-guide',
          title: 'Safety Guide'
        },
        ...baseActions
      ];
    }

    return baseActions;
  };

  // Simulate receiving notifications for testing
  const simulateNotifications = () => {
    // Emergency notification
    setTimeout(() => {
      showLocalNotification({
        title: 'Emergency Alert',
        body: 'Severe weather warning in your area. Seek shelter immediately.',
        type: 'emergency',
        severity: 'critical',
        location: 'Your Area'
      });
    }, 5000);

    // Disaster notification
    setTimeout(() => {
      showLocalNotification({
        title: 'Disaster Update',
        body: 'Wildfire reported 15 miles from your location. Monitor conditions.',
        type: 'disaster',
        severity: 'high',
        location: 'Northern California'
      });
    }, 10000);

    // Community notification
    setTimeout(() => {
      showLocalNotification({
        title: 'Community Alert',
        body: 'New emergency shelter opened at Community Center. 50 spaces available.',
        type: 'community',
        location: 'Downtown'
      });
    }, 15000);
  };

  // Handle notification clicks
  useEffect(() => {
    const handleNotificationClick = (event: Event) => {
      const notificationEvent = event as any;
      const { action, notification } = notificationEvent;
      const data = notification?.data;

      if (notification) {
        notification.close();
      }

      switch (action) {
        case 'call-911':
          // Open emergency call interface
          window.open('tel:911', '_self');
          break;
        
        case 'safety-guide':
          window.open('/safety', '_blank');
          break;
        
        case 'view':
          if (data?.url) {
            window.open(data.url, '_blank');
          } else {
            window.open('/dashboard', '_blank');
          }
          break;
        
        default:
          // Default action - focus the app
          window.focus();
          break;
      }
    };

    // Handle both service worker and direct notification clicks
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('notificationclick', handleNotificationClick);
    }

    // Handle direct notification clicks
    const handleDirectNotificationClick = (event: Event) => {
      const notification = event.target as Notification;
      if (notification?.data) {
        handleNotificationClick({
          action: 'view',
          notification
        } as any);
      }
    };

    // Add event listener for direct notifications
    if ('Notification' in window) {
      // Note: Direct notifications don't support click events in the same way
      // This is a limitation of browser APIs
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('notificationclick', handleNotificationClick);
      }
    };
  }, []);

  // For development - simulate notifications
  useEffect(() => {
    // Check if we're in development mode
    const isDevelopment = window.location.hostname === 'localhost' || 
                         window.location.hostname.includes('webcontainer') ||
                         window.location.hostname.includes('stackblitz');
    
    if (isDevelopment && capabilities.hasNotificationPermission) {
      const timer = setTimeout(simulateNotifications, 3000);
      return () => clearTimeout(timer);
    }
  }, [capabilities.hasNotificationPermission]);

  // Expose test function for manual testing
  useEffect(() => {
    // Add global test function for debugging
    (window as any).testNotification = () => {
      showLocalNotification({
        title: 'Test Notification',
        body: 'This is a test notification to verify functionality.',
        type: 'general'
      });
    };

    return () => {
      delete (window as any).testNotification;
    };
  }, []);

  return null; // This component doesn't render anything
};

export default PushNotificationManager;